import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'react-hot-toast';
import { Loader2, Search, Package, CheckCircle2, AlertTriangle, XCircle, Warehouse, RefreshCw } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ATPCheck = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [isChecking, setIsChecking] = useState(false);
  const [materials, setMaterials] = useState([]);
  const [plants, setPlants] = useState([]);
  
  const [request, setRequest] = useState({
    material_id: '',
    plant: '',
    storage_location: '',
    requested_quantity: 1,
    unit_of_measure: 'EA'
  });
  
  const [result, setResult] = useState(null);
  const [batchItems, setBatchItems] = useState([]);
  const [batchResults, setBatchResults] = useState([]);

  useEffect(() => {
    loadMasterData();
  }, []);

  const loadMasterData = async () => {
    setIsLoading(true);
    try {
      const [matRes, plantRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/materials`).catch(() => ({ data: [] })),
        axios.get(`${BACKEND_URL}/api/material-management/plants`).catch(() => ({ data: [] }))
      ]);
      setMaterials(matRes.data || []);
      setPlants(plantRes.data || []);
    } catch (error) {
      console.error('Error loading master data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCheck = async () => {
    if (!request.material_id) {
      toast.error('Material is required');
      return;
    }
    if (!request.requested_quantity || request.requested_quantity <= 0) {
      toast.error('Quantity must be greater than 0');
      return;
    }
    
    setIsChecking(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/otc/atp/check`, request);
      setResult(response.data);
      toast.success('ATP check completed');
    } catch (error) {
      console.error('Error checking ATP:', error);
      toast.error(error.response?.data?.detail || 'ATP check failed');
    } finally {
      setIsChecking(false);
    }
  };

  const handleAddBatchItem = () => {
    if (!request.material_id || !request.requested_quantity) {
      toast.error('Material and quantity required');
      return;
    }
    setBatchItems([...batchItems, { ...request, id: Date.now() }]);
    setRequest({ ...request, material_id: '', requested_quantity: 1 });
  };

  const handleRemoveBatchItem = (id) => {
    setBatchItems(batchItems.filter(item => item.id !== id));
  };

  const handleBatchCheck = async () => {
    if (batchItems.length === 0) {
      toast.error('Add items to batch first');
      return;
    }
    
    setIsChecking(true);
    try {
      const requests = batchItems.map(({ id, ...item }) => item);
      const response = await axios.post(`${BACKEND_URL}/api/otc/atp/check-batch`, requests);
      setBatchResults(response.data);
      toast.success('Batch ATP check completed');
    } catch (error) {
      console.error('Error batch checking ATP:', error);
      toast.error(error.response?.data?.detail || 'Batch ATP check failed');
    } finally {
      setIsChecking(false);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      'AVAILABLE': { color: 'bg-green-100 text-green-800', icon: CheckCircle2, text: 'Available' },
      'PARTIAL': { color: 'bg-yellow-100 text-yellow-800', icon: AlertTriangle, text: 'Partial' },
      'NOT_AVAILABLE': { color: 'bg-red-100 text-red-800', icon: XCircle, text: 'Not Available' }
    };
    const badge = badges[status] || { color: 'bg-gray-100 text-gray-800', icon: Package, text: status };
    const Icon = badge.icon;
    return (
      <span className={`inline-flex items-center gap-1 px-3 py-1 text-sm font-medium rounded-full ${badge.color}`}>
        <Icon className="w-4 h-4" /> {badge.text}
      </span>
    );
  };

  if (isLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-screen">
          <Loader2 className="w-8 h-8 animate-spin text-orange-600" />
          <span className="ml-3">Loading...</span>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6" data-testid="atp-check-page">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Availability Check (ATP)</h1>
            <p className="text-sm text-gray-600">Check material stock availability for sales orders</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Single Item Check */}
          <Card className="p-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Package className="w-5 h-5" /> Single Item Check
            </h3>
            <div className="space-y-4">
              <div>
                <Label>Material *</Label>
                <Select 
                  value={request.material_id || '__SELECT__'} 
                  onValueChange={(v) => setRequest({...request, material_id: v === '__SELECT__' ? '' : v})}
                >
                  <SelectTrigger><SelectValue placeholder="Select Material" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__SELECT__">Select Material</SelectItem>
                    {materials.map(m => (
                      <SelectItem key={m.material_number} value={m.material_number}>
                        {m.material_number} - {m.description}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Plant</Label>
                  <Select 
                    value={request.plant || '__ALL__'} 
                    onValueChange={(v) => setRequest({...request, plant: v === '__ALL__' ? '' : v})}
                  >
                    <SelectTrigger><SelectValue placeholder="All Plants" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__ALL__">All Plants</SelectItem>
                      {plants.map(p => (
                        <SelectItem key={p.plant_id} value={p.plant_id}>
                          {p.plant_id} - {p.plant_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Storage Location</Label>
                  <Input
                    value={request.storage_location}
                    onChange={(e) => setRequest({...request, storage_location: e.target.value})}
                    placeholder="Optional"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Requested Quantity *</Label>
                  <Input
                    type="number"
                    min="0.001"
                    step="0.001"
                    value={request.requested_quantity}
                    onChange={(e) => setRequest({...request, requested_quantity: parseFloat(e.target.value) || 0})}
                  />
                </div>
                <div>
                  <Label>Unit of Measure</Label>
                  <Select value={request.unit_of_measure} onValueChange={(v) => setRequest({...request, unit_of_measure: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="EA">EA - Each</SelectItem>
                      <SelectItem value="PC">PC - Piece</SelectItem>
                      <SelectItem value="KG">KG - Kilogram</SelectItem>
                      <SelectItem value="L">L - Liter</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="flex gap-2">
                <Button onClick={handleCheck} disabled={isChecking} className="flex-1" data-testid="check-atp-btn">
                  {isChecking ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Checking...</>
                  ) : (
                    <><Search className="w-4 h-4 mr-2" /> Check Availability</>
                  )}
                </Button>
                <Button variant="outline" onClick={handleAddBatchItem}>
                  Add to Batch
                </Button>
              </div>
            </div>

            {/* Single Result */}
            {result && (
              <div className="mt-6 border-t pt-6">
                <div className="flex justify-between items-center mb-4">
                  <h4 className="font-semibold">ATP Result</h4>
                  {getStatusBadge(result.status)}
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <p className="text-xs text-gray-600">Total Stock</p>
                    <p className="text-xl font-bold">{result.total_stock.toLocaleString()} {result.unit_of_measure}</p>
                  </div>
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <p className="text-xs text-gray-600">Reserved</p>
                    <p className="text-xl font-bold text-orange-600">{result.reserved_quantity.toLocaleString()} {result.unit_of_measure}</p>
                  </div>
                  <div className={`p-3 rounded-lg ${result.can_fulfill ? 'bg-green-50' : 'bg-red-50'}`}>
                    <p className="text-xs text-gray-600">Available</p>
                    <p className={`text-xl font-bold ${result.can_fulfill ? 'text-green-600' : 'text-red-600'}`}>
                      {result.available_quantity.toLocaleString()} {result.unit_of_measure}
                    </p>
                  </div>
                  <div className="bg-orange-50 p-3 rounded-lg">
                    <p className="text-xs text-gray-600">Requested</p>
                    <p className="text-xl font-bold text-orange-600">{result.requested_quantity.toLocaleString()} {result.unit_of_measure}</p>
                  </div>
                </div>
                
                {result.shortage_quantity > 0 && (
                  <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-red-800 font-medium">
                      ⚠️ Shortage: {result.shortage_quantity.toLocaleString()} {result.unit_of_measure}
                    </p>
                  </div>
                )}
                
                <div className="mt-4 p-3 bg-gray-100 rounded-lg">
                  <p className="text-sm">{result.message}</p>
                </div>
              </div>
            )}
          </Card>

          {/* Batch Check */}
          <Card className="p-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Warehouse className="w-5 h-5" /> Batch Check
            </h3>
            
            {batchItems.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Package className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                <p>No items in batch</p>
                <p className="text-sm">Add items from the single check form</p>
              </div>
            ) : (
              <>
                <div className="space-y-2 mb-4 max-h-60 overflow-y-auto">
                  {batchItems.map((item, index) => (
                    <div key={item.id} className="flex justify-between items-center p-2 bg-gray-50 rounded">
                      <div>
                        <span className="font-mono text-sm">{item.material_id}</span>
                        <span className="mx-2 text-gray-400">|</span>
                        <span>{item.requested_quantity} {item.unit_of_measure}</span>
                        {item.plant && <span className="text-gray-500 text-sm ml-2">@ {item.plant}</span>}
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => handleRemoveBatchItem(item.id)}>
                        <XCircle className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                  ))}
                </div>
                
                <Button onClick={handleBatchCheck} disabled={isChecking} className="w-full">
                  {isChecking ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Checking Batch...</>
                  ) : (
                    <><RefreshCw className="w-4 h-4 mr-2" /> Check All ({batchItems.length} items)</>
                  )}
                </Button>
              </>
            )}

            {/* Batch Results */}
            {batchResults.length > 0 && (
              <div className="mt-6 border-t pt-4">
                <h4 className="font-semibold mb-3">Batch Results</h4>
                <div className="space-y-3">
                  {batchResults.map((res, index) => (
                    <div key={index} className={`p-3 rounded-lg border ${
                      res.can_fulfill ? 'bg-green-50 border-green-200' : 
                      res.status === 'PARTIAL' ? 'bg-yellow-50 border-yellow-200' : 'bg-red-50 border-red-200'
                    }`}>
                      <div className="flex justify-between items-center">
                        <div>
                          <span className="font-mono font-medium">{res.material_id}</span>
                          <span className="text-sm text-gray-600 ml-2">
                            Req: {res.requested_quantity} | Avail: {res.available_quantity}
                          </span>
                        </div>
                        {getStatusBadge(res.status)}
                      </div>
                      {res.shortage_quantity > 0 && (
                        <p className="text-sm text-red-600 mt-1">
                          Shortage: {res.shortage_quantity} {res.unit_of_measure}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </Card>
        </div>
      </div>
    </Layout>
  );
};

export default ATPCheck;
